import {AttachFile, Close, Email, Person} from '@mui/icons-material';
import {Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography} from '@mui/material';
import {ContactCategory, ContactMessage} from '../../__generated__/resolvers-types';

export interface ContactMessageDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  message: ContactMessage | null;
}

export default function ContactMessageDetailsDialog({open, onClose, message}: ContactMessageDetailsDialogProps) {
  if (!message) return null;

  const getCategoryColor = (category: ContactCategory) => {
    switch (category) {
      case ContactCategory.SUPPORT:
        return 'error';
      case ContactCategory.FEEDBACK:
        return 'info';
      case ContactCategory.FEATURE_REQUEST:
        return 'warning';
      case ContactCategory.GENERAL:
        return 'primary';
      case ContactCategory.OTHER:
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Contact Message Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{mt: 1}}>
          {/* Header with sender info and category */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={2} sx={{flexGrow: 1}}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Typography variant="h6" fontWeight="medium">
                  {message.name}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" color="action" />
                <Typography variant="body1" color="text.secondary">
                  {message.email}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={message.category.replace('_', ' ')}
              color={getCategoryColor(message.category)}
              size="medium"
              variant="outlined"
            />
          </Box>

          {/* Message content */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Message
            </Typography>
            <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>
              {message.message}
            </Typography>
          </Box>

          {/* Attachments */}
          {message.attachments.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Attachments ({message.attachments.length})
              </Typography>
              <Stack spacing={1}>
                {message.attachments.map((attachment) => (
                  <Box
                    key={`ContactMessageDetailsDialog-attachment-${attachment.name}`}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <AttachFile fontSize="small" color="action" />
                    <Typography variant="body2">
                      {attachment.name} ({attachment.type})
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
